import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  AddEditContractorDocumentTemplate,
  ContractorDocumentTemplate,
} from "@/models/api/administration/contractors/ContractorDocumentTemplate";
import {
  addContractorDocumentTemplate,
  editContractorDocumentTemplate,
  getContractorDocumentTemplates,
  removeContractorDocumentTemplate,
  updateContractorPrintSetting,
} from "@/requests/administration/contractors/contractorDocumentTemplate";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { UpdateContractorPrintSetting } from "@/models/api/administration/contractors/UpdateContractorPrintSetting";

export interface AdministrationContractorDocumentTemplateInterface {
  contractorDocumentTemplates: Array<ContractorDocumentTemplate>;
  editDocumentTemplateModel: AddEditContractorDocumentTemplate;
  documentTypeName: string;
  documentTypeId: number | null;
  documentTemplateId: number | null;
  isDialog: boolean;
  onlyEndDate: boolean;
}

export const AdministrationContractorDocumentTemplateState: AdministrationContractorDocumentTemplateInterface = {
  contractorDocumentTemplates: [],
  editDocumentTemplateModel: new AddEditContractorDocumentTemplate(),
  documentTypeName: "",
  documentTypeId: null,
  documentTemplateId: null,
  isDialog: false,
  onlyEndDate: false,
};

const AdministrationContractorDocumentTemplateModule = defineModule({
  state: AdministrationContractorDocumentTemplateState as AdministrationContractorDocumentTemplateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setContractorDocumentTemplate(state, param: Array<ContractorDocumentTemplate>) {
      state.contractorDocumentTemplates = param;
    },
    setEditDocumentTemplateModel(state, param: AddEditContractorDocumentTemplate) {
      state.editDocumentTemplateModel = param;
    },
    setDocumentTypeName(state, param: string) {
      state.documentTypeName = param;
    },
    setDocumentTypeId(state, param: number | null) {
      state.documentTypeId = param;
    },
    setDocumentTemplateId(state, param: number | null) {
      state.documentTemplateId = param;
    },
    setIsDialog(state, param: boolean) {
      state.isDialog = param;
    },
    setOnlyEndDate(state, param: boolean) {
      state.onlyEndDate = param;
    },
  },
  actions: {
    async getContractorDocumentTemplate(context) {
      const { rootState, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId) {
        const result = await getContractorDocumentTemplates(
          rootState.administration.contractors.list.selectedContractorId
        );
        commit.setContractorDocumentTemplate(result);
      }
    },
    async addContractorDocumentTemplate(context, param: AddEditContractorDocumentTemplate) {
      const { rootState, state } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId && state.documentTypeId) {
        await addContractorDocumentTemplate(
          rootState.administration.contractors.list.selectedContractorId,
          state.documentTypeId,
          param
        );
      }
    },
    async editContractorDocumentTemplate(context, param: AddEditContractorDocumentTemplate) {
      const { rootState, state } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId && state.documentTemplateId) {
        await editContractorDocumentTemplate(
          rootState.administration.contractors.list.selectedContractorId,
          state.documentTemplateId,
          param
        );
      }
    },
    async removeContractorDocumentTemplate(context) {
      const { rootState, rootDispatch, state, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId && state.documentTemplateId) {
        const confirm = await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("areYouSureYouWantToDeleteThisDocumentTemplate")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "450px"
          )
        );
        if (confirm) {
          await removeContractorDocumentTemplate(
            rootState.administration.contractors.list.selectedContractorId,
            state.documentTemplateId
          );
          commit.setDocumentTemplateId(null);
        }
      }
    },
    async updateContractorPrintSetting(context, param: UpdateContractorPrintSetting) {
      const { rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId) {
        await updateContractorPrintSetting(rootState.administration.contractors.list.selectedContractorId, param);
        await dispatch.getContractorDocumentTemplate();
      }
    },
  },
});

export default AdministrationContractorDocumentTemplateModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorDocumentTemplateModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorDocumentTemplateModule);
